/**
 * The motif library — what is drawn INSIDE a planet's disc.
 *
 * Every entry is transcribed from the reference field's `motifInner`
 * switch, with the per-planet coordinates kept verbatim and the
 * per-planet COLOURS lifted out into the derived palette.  That is the
 * whole port: the reference drew 34 named worlds, this draws the same
 * two dozen visual ideas for whatever worlds the registry holds.
 *
 * `mono` is the cold state — a planet nobody has opened shows a ghost of
 * its motif rather than nothing, so a learner can see what waits.
 */

import type { ReactNode } from "react";

import type { Palette } from "./palette";
import type { MotifKey, PlanetLook } from "./planetLook";

type Args = {
  look: PlanetLook;
  /** Disc radius in viewBox units. */
  s: number;
  P: Palette;
  mono: boolean;
};

export function Motif({ motif, look, s, P, mono }: Args & { motif: MotifKey }): ReactNode {
  const sw = Math.max(0.45, s * 0.11);
  const o = mono ? 0.4 : 0.9;
  const ink = mono ? P.dim : look.lo;
  const lit = mono ? P.dim : look.hi;
  const spot = mono ? P.dim : look.spot;

  switch (motif) {
    case "bands":
      return (
        <>
          {[-0.38, 0, 0.38].map((y, i) => (
            <path
              key={i}
              d={`M ${-0.85 * s} ${y * s} Q 0 ${(y + 0.22) * s} ${0.85 * s} ${y * s}`}
              fill="none"
              stroke={ink}
              strokeOpacity={o * 0.85}
              strokeWidth={sw}
            />
          ))}
        </>
      );

    case "equator":
      return (
        <>
          <ellipse rx={0.42 * s} ry={s} fill="none" stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw} />
          <line x1={-s} y1="0" x2={s} y2="0" stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw} />
          <path
            d={`M ${-0.9 * s} ${-0.42 * s} Q 0 ${-0.6 * s} ${0.9 * s} ${-0.42 * s}`}
            fill="none" stroke={ink} strokeOpacity={o * 0.6} strokeWidth={sw}
          />
          <path
            d={`M ${-0.9 * s} ${0.42 * s} Q 0 ${0.6 * s} ${0.9 * s} ${0.42 * s}`}
            fill="none" stroke={ink} strokeOpacity={o * 0.6} strokeWidth={sw}
          />
        </>
      );

    case "eyes":
      return (
        <>
          <circle cx={0.16 * s} cy={0.2 * s} r={0.58 * s} fill="none" stroke={ink} strokeOpacity={o * 0.75} strokeWidth={sw} />
          <circle cx={0.16 * s} cy={0.2 * s} r={0.3 * s} fill="none" stroke={ink} strokeOpacity={o * 0.85} strokeWidth={sw} />
          <circle cx={0.16 * s} cy={0.2 * s} r={0.07 * s} fill={ink} fillOpacity={o} />
        </>
      );

    case "terminator":
      return (
        <>
          <path d={`M ${-s} 0 A ${s} ${s} 0 0 0 ${s} 0 Z`} fill="#0A1428" fillOpacity={mono ? 0.12 : 0.34} />
          <line x1={-s} y1="0" x2={s} y2="0" stroke={mono ? P.dim : "#EAF2F8"} strokeOpacity={o * 0.8} strokeWidth={sw} />
        </>
      );

    case "hex": {
      const hex = [...Array(6)]
        .map((_, i) => {
          const a = ((i * 60 - 90) * Math.PI) / 180;
          return `${(0.78 * s * Math.cos(a)).toFixed(2)} ${(0.78 * s * Math.sin(a)).toFixed(2)}`;
        })
        .join(" L ");
      return (
        <>
          <path d={`M ${hex} Z`} fill="none" stroke={ink} strokeOpacity={o * 0.85} strokeWidth={sw} />
          <line x1="0" y1={-0.78 * s} x2="0" y2={0.78 * s} stroke={ink} strokeOpacity={o * 0.55} strokeWidth={sw} />
        </>
      );
    }

    case "speckles":
      return (
        <>
          {[[-0.35, -0.3], [0.3, -0.35], [-0.15, 0.3], [0.4, 0.25], [0.05, -0.05]].map(([x, y], i) => (
            <circle key={i} cx={x * s} cy={y * s} r={0.11 * s} fill={ink} fillOpacity={o * (0.65 + 0.07 * i)} />
          ))}
        </>
      );

    case "zigzag":
      return (
        <path
          d={`M ${-0.75 * s} ${-0.35 * s} L ${-0.2 * s} ${0.15 * s} L ${0.2 * s} ${-0.2 * s} L ${0.75 * s} ${0.35 * s}`}
          fill="none" stroke={ink} strokeOpacity={o} strokeWidth={sw * 1.2} strokeLinejoin="round"
        />
      );

    case "target":
      return (
        <>
          <circle cx={0.14 * s} cy={-0.1 * s} r={0.6 * s} fill="none" stroke={ink} strokeOpacity={o * 0.8} strokeWidth={sw} />
          <circle cx={0.14 * s} cy={-0.1 * s} r={0.3 * s} fill="none" stroke={ink} strokeOpacity={o * 0.85} strokeWidth={sw} />
          <circle cx={0.14 * s} cy={-0.1 * s} r={0.08 * s} fill={lit} fillOpacity={o * 0.95} />
          <circle cx={-0.34 * s} cy={0.3 * s} r={0.09 * s} fill={spot} fillOpacity={o} />
        </>
      );

    case "strata":
      return (
        <>
          {[[-0.52, 0.34, 0.9], [-0.14, 0.2, 0.5], [0.1, 0.3, 0.7], [0.44, 0.18, 0.35]].map(
            ([y, h, op], i) => (
              <rect key={i} x={-0.9 * s} y={y * s} width={1.8 * s} height={h * s} fill={ink} fillOpacity={o * op} />
            ),
          )}
        </>
      );

    case "cutbar":
      return (
        <>
          <rect x={-0.85 * s} y={-0.16 * s} width={0.72 * s} height={0.32 * s} fill={ink} fillOpacity={o * 0.85} />
          <rect x={-0.13 * s} y={-0.16 * s} width={0.98 * s} height={0.32 * s} fill={ink} fillOpacity={o * 0.22} />
          <line x1={-0.13 * s} y1={-0.42 * s} x2={-0.13 * s} y2={0.42 * s} stroke={spot} strokeOpacity={o} strokeWidth={sw * 1.1} />
        </>
      );

    case "grid":
      return (
        <>
          {[-0.3, 0.3].map((x, i) => (
            <line key={`v${i}`} x1={x * s} y1={-0.9 * s} x2={x * s} y2={0.9 * s} stroke={ink} strokeOpacity={o * 0.8} strokeWidth={sw} />
          ))}
          <line x1={-0.9 * s} y1="0" x2={0.9 * s} y2="0" stroke={ink} strokeOpacity={o * 0.8} strokeWidth={sw} />
          {[-0.3, 0.3].map((x, i) => (
            <circle key={`j${i}`} cx={x * s} cy="0" r={0.09 * s} fill={lit} fillOpacity={o * 0.9} />
          ))}
        </>
      );

    case "shelves":
      return (
        <>
          <path
            d={`M ${-0.95 * s} ${-0.3 * s} Q 0 ${-0.42 * s} ${0.95 * s} ${-0.3 * s} L ${0.95 * s} ${-0.12 * s} Q 0 ${-0.24 * s} ${-0.95 * s} ${-0.12 * s} Z`}
            fill={ink} fillOpacity={o * 0.4}
          />
          <path
            d={`M ${-0.95 * s} ${0.3 * s} Q 0 ${0.42 * s} ${0.95 * s} ${0.3 * s} L ${0.95 * s} ${0.5 * s} Q 0 ${0.62 * s} ${-0.95 * s} ${0.5 * s} Z`}
            fill={ink} fillOpacity={o * 0.35}
          />
          <ellipse
            cx={0.32 * s} cy={0.06 * s} rx={0.26 * s} ry={0.18 * s}
            fill={spot} fillOpacity={o * 0.9} stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw * 0.7}
          />
        </>
      );

    case "rules":
      return (
        <>
          {[-0.48, -0.16, 0.16, 0.48].map((y, i) => (
            <line
              key={i} x1={-0.92 * s} y1={y * s} x2={0.92 * s} y2={y * s}
              stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw * (i % 2 ? 1.4 : 0.9)}
            />
          ))}
        </>
      );

    case "crescent":
      return (
        <path
          d={`M 0 ${-s} A ${s} ${s} 0 0 1 0 ${s} A ${0.55 * s} ${s} 0 0 0 0 ${-s} Z`}
          fill={mono ? P.dim : "#FFF7E2"} fillOpacity={mono ? 0.15 : 0.42}
        />
      );

    case "split":
      return (
        <>
          <path d={`M 0 ${-s} A ${s} ${s} 0 0 0 0 ${s} Z`} fill={spot} fillOpacity={mono ? 0.18 : 0.7} />
          <line x1="0" y1={-s} x2="0" y2={s} stroke={mono ? P.dim : "#F2E6D2"} strokeOpacity={o * 0.7} strokeWidth={sw} />
        </>
      );

    case "stream":
      return (
        <>
          <rect x={-s} y={-0.16 * s} width={2 * s} height={0.32 * s} fill={lit} fillOpacity={o * 0.55} />
          <line x1={-s} y1="0" x2={s} y2="0" stroke={mono ? P.dim : "#F2F9FF"} strokeOpacity={o * 0.7} strokeWidth={sw * 0.8} />
        </>
      );

    case "histogram":
      return (
        <>
          {[[-0.55, 0.5], [-0.18, 0.95], [0.19, 0.62], [0.56, 0.34]].map(([x, h], i) => (
            <rect
              key={i} x={(x - 0.13) * s} y={(0.78 - h * 0.95) * s} width={0.26 * s} height={h * 0.95 * s}
              fill={ink} fillOpacity={o * (i === 1 ? 0.9 : 0.45)}
            />
          ))}
          <circle cx={-0.18 * s} cy={-0.52 * s} r={0.13 * s} fill={lit} fillOpacity={o * 0.95} />
          <line x1={-0.18 * s} y1={-0.38 * s} x2={-0.18 * s} y2={-0.24 * s} stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw} />
        </>
      );

    case "window":
      return (
        <>
          <rect
            x={-0.55 * s} y={-0.4 * s} width={1.1 * s} height={0.8 * s} rx={0.08 * s}
            fill="none" stroke={ink} strokeOpacity={o * 0.9} strokeWidth={sw}
          />
          {[-0.3, 0, 0.3].map((x, i) => (
            <circle key={`i${i}`} cx={x * s} cy="0" r={0.09 * s} fill={ink} fillOpacity={o * 0.85} />
          ))}
          {[-0.82, 0.82].map((x, i) => (
            <circle key={`o${i}`} cx={x * s} cy="0" r={0.08 * s} fill={ink} fillOpacity={o * 0.25} />
          ))}
        </>
      );

    case "mask":
      return (
        <>
          <circle cx="0" cy={0.08 * s} r={0.62 * s} fill="none" stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw} />
          <path
            d={`M ${-0.62 * s} ${-0.18 * s} Q 0 ${-0.74 * s} ${0.62 * s} ${-0.18 * s} L ${0.5 * s} ${0.12 * s} Q 0 ${-0.32 * s} ${-0.5 * s} ${0.12 * s} Z`}
            fill={spot} fillOpacity={o * 0.75} stroke={ink} strokeOpacity={o * 0.6} strokeWidth={sw * 0.7}
          />
        </>
      );

    case "twin":
      return (
        <>
          <circle
            cx={-0.16 * s} cy={0.16 * s} r={0.5 * s} fill="none" stroke={ink}
            strokeOpacity={o * 0.5} strokeWidth={sw} strokeDasharray={`${0.09 * s} ${0.12 * s}`}
          />
          <circle
            cx={0.15 * s} cy={-0.12 * s} r={0.5 * s} fill={lit} fillOpacity={o * 0.5}
            stroke={ink} strokeOpacity={o * 0.85} strokeWidth={sw}
          />
        </>
      );

    case "flow":
      return (
        <>
          {[-0.3, 0, 0.3].map((y, i) => (
            <path
              key={`f${i}`}
              d={`M ${-0.8 * s} ${y * s} C ${-0.15 * s} ${y * s} ${0.1 * s} ${(y - 0.24) * s} ${0.8 * s} ${(y - 0.32) * s}`}
              fill="none" stroke={ink} strokeOpacity={o * (i === 1 ? 0.9 : 0.45)} strokeWidth={sw}
            />
          ))}
          <circle cx={-0.32 * s} cy={0.36 * s} r={0.12 * s} fill={lit} fillOpacity={o * 0.95} />
        </>
      );

    case "ladder":
      return (
        <>
          {[-0.42, 0.42].map((x, i) => (
            <line key={`r${i}`} x1={x * s} y1={-0.7 * s} x2={x * s} y2={0.7 * s} stroke={ink} strokeOpacity={o * 0.7} strokeWidth={sw} />
          ))}
          {[-0.45, -0.15, 0.15, 0.45].map((y, i) => (
            <line
              key={`g${i}`} x1={-0.42 * s} y1={y * s} x2={0.42 * s} y2={y * s}
              stroke={ink} strokeOpacity={o * (i === 1 ? 1 : 0.5)} strokeWidth={sw * (i === 1 ? 1.4 : 1)}
            />
          ))}
        </>
      );

    case "gauge":
      return (
        <>
          <path
            d={`M ${-0.55 * s} ${0.25 * s} A ${0.55 * s} ${0.55 * s} 0 0 1 ${0.55 * s} ${0.25 * s}`}
            fill="none" stroke={ink} strokeOpacity={o * 0.85} strokeWidth={sw}
          />
          <line x1="0" y1={0.25 * s} x2={0.3 * s} y2={-0.3 * s} stroke={ink} strokeOpacity={o} strokeWidth={sw} />
          {[-0.3, 0, 0.3].map((x, i) => (
            <circle key={`d${i}`} cx={x * s} cy={0.55 * s} r={0.07 * s} fill={ink} fillOpacity={o * 0.6} />
          ))}
        </>
      );

    case "wire":
      return (
        <>
          <path
            d={`M ${-0.7 * s} ${0.3 * s} L ${-0.2 * s} ${0.3 * s} L ${-0.2 * s} ${-0.3 * s} L ${0.7 * s} ${-0.3 * s}`}
            fill="none" stroke={ink} strokeOpacity={o * 0.9} strokeWidth={sw}
          />
          <circle cx={-0.7 * s} cy={0.3 * s} r={0.11 * s} fill={ink} fillOpacity={o} />
          <circle cx={0.7 * s} cy={-0.3 * s} r={0.11 * s} fill={lit} fillOpacity={o * 0.95} />
        </>
      );

    case "seams":
      return (
        <>
          <path
            d={`M ${-0.25 * s} ${-s} L ${-0.02 * s} ${-0.3 * s} L ${-0.3 * s} ${0.25 * s} L ${-0.05 * s} ${s}`}
            fill="none" stroke={ink} strokeOpacity={o} strokeWidth={sw}
          />
          <path
            d={`M ${0.45 * s} ${-s} L ${0.3 * s} ${-0.1 * s} L ${0.55 * s} ${s}`}
            fill="none" stroke={ink} strokeOpacity={o * 0.75} strokeWidth={sw}
          />
        </>
      );

    case "wrapper":
      return (
        <>
          <rect
            x={-0.6 * s} y={-0.6 * s} width={1.2 * s} height={1.2 * s} rx={0.14 * s}
            fill="none" stroke={ink} strokeOpacity={o * 0.8} strokeWidth={sw}
            strokeDasharray={`${0.12 * s} ${0.1 * s}`}
          />
          <circle r={0.26 * s} fill={ink} fillOpacity={o * 0.8} />
        </>
      );

    default:
      return null;
  }
}
